import type { SpeakOptionsModel } from "../../type/speak";
import { DEFAULT_LOCALE_TAGS } from "../../utils/config";

let cachedVoices: SpeechSynthesisVoice[] = [];

// Some mobile browsers (Android Chrome, iOS Safari) never fire `onvoiceschanged`
// and only populate `getVoices()` after a short delay, so poll with a timeout
// instead of waiting on the event forever.
const loadVoices = (): Promise<SpeechSynthesisVoice[]> =>
  new Promise((resolve) => {
    const existing = window.speechSynthesis.getVoices();
    if (existing.length > 0) {
      cachedVoices = existing;
      resolve(existing);
      return;
    }

    let settled = false;
    const finish = (voices: SpeechSynthesisVoice[]) => {
      if (settled) return;
      settled = true;
      clearInterval(pollId);
      clearTimeout(timeoutId);
      cachedVoices = voices;
      resolve(voices);
    };

    window.speechSynthesis.onvoiceschanged = () => finish(window.speechSynthesis.getVoices());

    const pollId = setInterval(() => {
      const voices = window.speechSynthesis.getVoices();
      if (voices.length > 0) finish(voices);
    }, 100);

    // Give up waiting for a voice list and let the caller fall back to defaults.
    const timeoutId = setTimeout(() => finish(window.speechSynthesis.getVoices()), 2000);
  });

// Warm up the voice list as early as possible so it is already cached by the
// time the user taps "Speak" (mobile browsers otherwise need this first call
// to happen outside of any user-gesture handler).
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  loadVoices();
}

/**
 * Resolve a full BCP-47 locale tag for a given language code/tag.
 */
export const toLocaleTag = (lang: string): string => {
  const normalized = lang.toLowerCase();
  if (normalized.includes("-")) return lang;
  return DEFAULT_LOCALE_TAGS[normalized] ?? lang;
};

/**
 * Resolve best available voice for requested language.
 * `lang` can be a short code (e.g. "es") or a full BCP-47 tag (e.g. "es-ES").
 */
const getVoiceForLang = async (
  lang: string
): Promise<SpeechSynthesisVoice | null> => {
  const voices = cachedVoices.length ? cachedVoices : await loadVoices();

  if (!voices.length) return null;

  const target = lang.toLowerCase();
  const primary = target.split("-")[0];

  return (
    voices.find(v => v.lang.toLowerCase() === target) ||
    voices.find(v => v.lang.toLowerCase().startsWith(`${primary}-`)) ||
    voices.find(v => v.lang.toLowerCase() === primary) ||
    null
  );
};


/**
 * Speak text with progress callbacks
 */
export const speak = async (
  text: string,
  lang: string = "en",
  rate = 0.9,
  options?: SpeakOptionsModel
): Promise<void> => {
  if (!("speechSynthesis" in window) || !text.trim()) return;

  const voice = await getVoiceForLang(lang);

  // No matching voice installed — still attempt to speak using the plain
  // locale tag so the device's default TTS engine can pick a voice itself
  // (common on mobile, which ships far fewer distinct voices than desktop).
  if (!voice) {
    console.warn(`No exact TTS voice match for language "${lang}" on this device, using default.`);
    options?.onMissingVoice?.(lang);
  }

  return new Promise<void>((resolve) => {
    const utter = new SpeechSynthesisUtterance(text);

    utter.lang = voice ? voice.lang : toLocaleTag(lang);
    utter.rate = rate;
    utter.pitch = 1;
    utter.volume = 1;
    if (voice) utter.voice = voice;

    const totalChars = text.length || 1;

    utter.onstart = () => {
      options?.onStart?.();
    };

    utter.onboundary = (event: SpeechSynthesisEvent) => {
      if (typeof event.charIndex === "number") {
        const progress = Math.min(event.charIndex / totalChars, 1);
        options?.onProgress?.(progress);
      }
    };

    utter.onend = () => {
      options?.onProgress?.(1);
      options?.onEnd?.();
      resolve();
    };

    utter.onerror = () => {
      options?.onEnd?.();
      resolve();
    };

    window.speechSynthesis.cancel(); // prevent overlapping speech
    window.speechSynthesis.speak(utter);
  });
};

/**
 * Cancel active speech
 */
export const cancelSpeech = (): void => {
  if ("speechSynthesis" in window) {
    window.speechSynthesis.cancel();
  }
};
